import React, { useState, useCallback, useEffect } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import Draggable from "react-draggable";
import { animations } from "../../../../engine/modules/camera/animations";
import { dockFloatingGalleryOnly } from "../../Common/modalDocking.js";
import { DOCK_TO_LEFT_ICON, DOCK_TO_LEFT_ICON_CLASS } from "../../Common/dockControls.js";
import { shouldIgnoreShortcut } from "../../../utils/hotkeys.js";

const CAMERA_SET_SHORTCUT = "Alt+C";
const STATE_SET_SHORTCUT = "Alt+T";
const UPDATE_BOTH_SHORTCUT = "Alt+U";

function isAltKeyShortcut(event, code) {
  return event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey && event.code === code;
}

function DragHandle({ listeners, attributes }) {
  return (
    <div className="KeyframeDragStyle" {...listeners} {...attributes}>
      <i className="material-icons notranslate">drag_indicator</i>
    </div>
  );
}

function SortableKeyframe({ data, index, disabled, editing, keyframeCount, keyframeIndex, isActive, toggleGallery, currentKeyframeId }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: data.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 1,
    position: isDragging ? "relative" : undefined,
  };

  // Check if this keyframe is active by comparing IDs
  const isThisActive = data.id === currentKeyframeId;

  return (
    <div ref={setNodeRef} style={style} id={`keyframe-${data.id}`} data-dragging={isDragging}>
      <Keyframe
        data={data}
        editing={editing}
        keyframeCount={keyframeCount}
        keyframeIndex={keyframeIndex}
        isActive={isThisActive}
        toggleGallery={toggleGallery}
        dragHandleListeners={listeners}
        dragHandleAttributes={attributes}
        isDragging={isDragging}
      />
    </div>
  );
}

function Keyframe({
  data,
  editing,
  keyframeCount,
  keyframeIndex,
  isActive,
  toggleGallery,
  dragHandleListeners,
  dragHandleAttributes,
  isDragging,
}) {
  const handleClick = () => {
    Inventum.animations.showKeyframe(data.id);
  };

  const handleDelete = () => {
    Inventum.animations.deleteKeyframe(data.id);
  };

  return (
    <div
      className={
        "KeyframePreview" +
        (data.enabled ? "" : " disabled") +
        (isActive ? " active" : "") +
        (editing ? " editing" : "") +
        (isDragging ? " dragging" : "")
      }
      onClick={handleClick}>
      {editing ? <DragHandle listeners={dragHandleListeners} attributes={dragHandleAttributes} /> : null}
      <KeyframeThumbnail
        id={data.id}
        flightDuration={data.flightDuration}
        editing={editing}
        keyframeCount={keyframeCount}
        keyframeIndex={keyframeIndex}
        path={data.thumbnail}
        base64Thumb={data.thumbnailBase64}
        delay={data.delay}
      />
      <div style={{ width: "90%", marginRight: "20px", position: "relative" }}>
        <KeyframeDescription editing={editing} keyframeIndex={keyframeIndex} description={data.description} name={data.name} />
        <KeyframeLabelIndicator scenestate={data.scenestate} />
      </div>
      {editing ? <KeyframeEditButtonPanel id={data.id} /> : null}
      {editing ? (
        <div title="Delete Action" onClick={handleDelete} className="KeyframeDeleteButton">
          <i className="material-icons notranslate">delete</i>
        </div>
      ) : null}
    </div>
  );
}

function KeyframeContainer({ keyframes, editing, toggleGallery, currentKeyframe }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Track the currently active keyframe by its properties (not just position)
  const [activeKeyframeProps, setActiveKeyframeProps] = React.useState(null);
  
  // Update active keyframe props when currentKeyframe changes
  React.useEffect(() => {
    if (currentKeyframe > 0 && keyframes[currentKeyframe - 1]) {
      const kf = keyframes[currentKeyframe - 1];
      setActiveKeyframeProps({
        name: kf.name,
        flightDuration: kf.flightDuration,
        delay: kf.delay,
        scenestate: kf.scenestate
      });
    }
  }, [currentKeyframe, keyframes]);

  // Find which keyframe ID is currently active by matching properties
  const currentKeyframeId = React.useMemo(() => {
    if (!activeKeyframeProps) return null;
    
    // First try to match by position (most common case)
    if (currentKeyframe > 0 && keyframes[currentKeyframe - 1]) {
      const kf = keyframes[currentKeyframe - 1];
      if (kf.name === activeKeyframeProps.name && 
          kf.scenestate === activeKeyframeProps.scenestate &&
          kf.delay === activeKeyframeProps.delay) {
        return kf.id;
      }
    }
    
    // If position doesn't match (after reorder), find by properties
    for (let i = 0; i < keyframes.length; i++) {
      const kf = keyframes[i];
      if (kf.name === activeKeyframeProps.name && 
          kf.scenestate === activeKeyframeProps.scenestate &&
          kf.delay === activeKeyframeProps.delay) {
        return kf.id;
      }
    }
    
    return null;
  }, [activeKeyframeProps, keyframes, currentKeyframe]);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = keyframes.findIndex((item) => item.id === active.id);
        const newIndex = keyframes.findIndex((item) => item.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          Inventum.animations.reorderKeyframes(oldIndex, newIndex);

          // Add drop animation
          const targetElement = document.getElementById(`keyframe-${over.id}`);
          if (targetElement) {
            targetElement.classList.add("drop-animation");
            setTimeout(() => targetElement.classList.remove("drop-animation"), 200);
          }
        }
      }
    },
    [keyframes]
  );

  return (
    <div className="KeyframeContainer">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}>
        <div className="SortableKeyframeList">
          <SortableContext items={keyframes.map((keyframe) => keyframe.id)} strategy={verticalListSortingStrategy}>
            {keyframes.map((keyData, index) => (
              <SortableKeyframe
                key={keyData.id}
                data={keyData}
                index={index}
                disabled={!editing}
                editing={editing}
                keyframeCount={keyframes.length}
                keyframeIndex={index + 1}
                currentKeyframeId={currentKeyframeId}
                toggleGallery={toggleGallery}
              />
            ))}
          </SortableContext>
        </div>
      </DndContext>

      {editing ? <CreateNewKeyframe /> : null}
    </div>
  );
}

function KeyframeEditButtonPanel({ id }) {
  const handleClone = () => {
    Inventum.animations.cloneKeyframe(id);
  };

  return (
    <div className="KeyframeEditButtonPanel">
      <div className="EditActionButton" title="Clones Action" onClick={handleClone}>
        Clone Action
      </div>
    </div>
  );
}

function KeyframeThumbnail({ id, flightDuration, editing, keyframeCount, keyframeIndex, path, base64Thumb, delay }) {
  let imagePath = "data/thumbnails/placeholder.png";
  if (base64Thumb) {
    imagePath = base64Thumb;
  } else if (path) {
    imagePath = path;
  }

  const style = {
    backgroundImage: `url(${imagePath})`,
  };

  const handleDelayClick = (e) => {
    if (!editing) return;
    e.stopPropagation();
    Inventum.animations.changeDelay(keyframeIndex - 1);
  };

  const handleFlightTime = (e) => {
    Inventum.animations.setFlightDuration(id);
    e.stopPropagation();
  };

  return (
    <div className="KeyframeThumbnail" style={style}>
      <div className="KeyframeInfo">
        <div className="KeyframeCounter">
          {keyframeIndex} / {keyframeCount}
        </div>
        {editing ? (
          <div
            style={{ fontSize: "12px", paddingRight: "5px" }}
            title={editing ? "Click to change camera flight duration" : "Action Duration"}
            className={`KeyframeDelay${editing ? " editable" : ""}`}
            onClick={handleFlightTime}>
            FD: {(flightDuration || 3000) / 1000}
          </div>
        ) : null}
        <div
          style={{ fontSize: "12px" }}
          title={editing ? "Click to change action duration" : "Action Duration"}
          className={`KeyframeDelay${editing ? " editable" : ""}`}
          onClick={handleDelayClick}>
          {editing ? "AD" : "Delay"}: {delay / 1000}
        </div>
      </div>
    </div>
  );
}

function CreateNewKeyframe() {
  const handleClick = (e) => {
    e.stopPropagation();
    Inventum.animations.createKeyframe();
  };

  return (
    <div className="CreateKeyframe" onClick={handleClick}>
      Create Action
    </div>
  );
}

function KeyframeLabelIndicator({ scenestate }) {
  // Get initial state from scene state
  const getInitialState = () => {
    if (scenestate) {
      const sceneState = Inventum.sceneStates.getSceneStateByID(scenestate);
      if (sceneState && sceneState.labelsToggleState !== undefined) {
        return sceneState.labelsToggleState;
      }
    }
    return true;
  };
  
  const [labelsEnabled, setLabelsEnabled] = React.useState(getInitialState);
  
  // Update label state on every render
  useEffect(() => {
    const updateLabelState = () => {
      if (scenestate) {
        const sceneState = Inventum.sceneStates.getSceneStateByID(scenestate);
        if (sceneState && sceneState.labelsToggleState !== undefined) {
          setLabelsEnabled(sceneState.labelsToggleState);
        }
      }
    };
    
    updateLabelState();
    
    // Poll every 150ms to catch state changes
    const intervalId = setInterval(updateLabelState, 150);
    
    return () => clearInterval(intervalId);
  }, [scenestate]);

  const labelIconColor = labelsEnabled ? "#4CAF50" : "#F44336";

  return (
    <div 
      className="KeyframeLabelIndicator"
      title={labelsEnabled ? "Labels enabled for this scene" : "Labels disabled for this scene"}
      style={{ color: labelIconColor }}
    >
      <i className="material-icons notranslate">label</i>
    </div>
  );
}

function KeyframeDescription({ editing, keyframeIndex, description, name }) {
  const handleNameClick = (e) => {
    if (!editing) return;
    e.stopPropagation();
    Inventum.animations.changeName(keyframeIndex - 1);
  };

  return (
    <div className="KeyframeDescription" style={{ width: "100%" }}>
      <div
        title={editing ? "Click to change action title" : "Animation Title"}
        className={`KeyframeName${editing ? " editable" : ""}`}
        onClick={handleNameClick}>
        {name}
      </div>
    </div>
  );
}

// Empty state component when no animation is selected
function GalleryEmptyState({ prepareRightDockForAnimationLoad }) {
  const [availableAnimations, setAvailableAnimations] = useState([]);

  useEffect(() => {
    // Get the list of available animations (now includes keyframeCount)
    const animationList = Inventum.animations.getAnimations();
    setAvailableAnimations(animationList || []);
  }, []);

  const handleSelectAnimation = (animation) => {
    // Select the animation when clicked using ID (not name)
    if (animation && animation.id) {
      if (prepareRightDockForAnimationLoad) {
        prepareRightDockForAnimationLoad();
      }
      Inventum.animations.selectAnimation(animation.id);
    }
  };

  return (
    <div className="GalleryEmptyState">
      <div className="EmptyStateHeader">
        <i className="material-icons notranslate">movie</i>
        <h3>No Animation Selected</h3>
      </div>
      
      <div className="EmptyStateMessage">
        <p>To view content in the gallery, you need to select an animation first.</p>
      </div>

      {availableAnimations && availableAnimations.length > 0 ? (
        <>
          <div className="EmptyStateSubheader">Available Animations:</div>
          <div className="AvailableAnimationsList">
            {availableAnimations.map((animation, index) => (
              <div
                key={animation.id || index}
                className="AvailableAnimationItem"
                onClick={() => handleSelectAnimation(animation)}
                title={`Click to select "${animation.name || 'Unnamed'}"`}
              >
                <i className="material-icons notranslate">play_circle_outline</i>
                <span className="AnimationName">{animation.name || 'Unnamed Animation'}</span>
                {animation.keyframeCount !== undefined && (
                  <span className="AnimationActions">
                    {animation.keyframeCount} {animation.keyframeCount === 1 ? 'action' : 'actions'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="EmptyStateNoAnimations">
          <i className="material-icons notranslate">info_outline</i>
          <p>No animations are available in this project.</p>
        </div>
      )}
    </div>
  );
}

function GalleryContainer({ canAccessEditorUI, reviewMode, galleryMinimized, galleryVisible, toggleGalleryMin, toggleGallery, sceneKey, isDocked, toggleGalleryDocked, toggleRightDock, setActiveTab, prepareRightDockForAnimationLoad }) {
  const [state, setState] = useState({
    keyframes: [],
    name: "No Animation Selected",
    isTour: false,
    cameraLocked: true,
    currentKeyframe: null,
  });
  const draggableRef = React.useRef(null);

  const updateAnimationState = useCallback((animationState) => {
    setState(animationState);
  }, []);

  const registerHandlers = useCallback(() => {
    Inventum.animations.registerStateCallback(updateAnimationState);
    const x = Inventum.animations.getState();
    updateAnimationState(x);
  }, [updateAnimationState]);

  useEffect(() => {
    registerHandlers();
  }, [registerHandlers]);

  useEffect(() => {
    if (sceneKey) {
      registerHandlers();
    }
  }, [sceneKey, registerHandlers]);

  // Auto-close gallery if animation becomes unselected (only for floating)
  useEffect(() => {
    if (state.name === "No Animation Selected" && galleryVisible && !isDocked) {
      toggleGallery(false);
    }
  }, [state.name, galleryVisible, toggleGallery, isDocked]);

  const toggleCameraLock = useCallback(() => {
    Inventum.animations.toggleCameraLock();
    setState((prevState) => ({ ...prevState, cameraLocked: !prevState.cameraLocked }));
  }, []);

  let showEditor = false;
  if (canAccessEditorUI && reviewMode && state.name !== "No Animation Selected") {
    showEditor = true;
  }

  // Handle docking the gallery
  const handleDock = () => {
    toggleGallery(false); // Close floating gallery
    if (toggleGalleryDocked) toggleGalleryDocked(true);
    if (toggleRightDock) toggleRightDock(true);
    if (setActiveTab) setActiveTab('gallery');
  };

  const handleCloseToDock = () => {
    dockFloatingGalleryOnly({ toggleGallery, toggleGalleryDocked });
  };

  // Check if we should show empty state (only when docked and no animation selected)
  const showEmptyState = isDocked && state.name === "No Animation Selected";

  // Content to render
  const galleryContent = (
    <>
      {!isDocked && (
        <GalleryHeader
          galleryMinimized={galleryMinimized}
          editing={showEditor}
          toggleGalleryMin={toggleGalleryMin}
          toggleGallery={toggleGallery}
          animationName={state.name}
          isDocked={isDocked}
          onDock={handleDock}
          onCloseToDock={handleCloseToDock}
        />
      )}
      {isDocked && !showEmptyState && (
        <div className="DockedGalleryTitleBar">
          <GalleryTitle galleryMinimized={false} editing={showEditor} animationName={state.name} isDocked={isDocked} />
        </div>
      )}
      {showEmptyState ? (
        <GalleryEmptyState prepareRightDockForAnimationLoad={prepareRightDockForAnimationLoad} />
      ) : (
        <>
          {showEditor && (
            <div className="EditHeadersContainer">
              <EditAnimationHeader
                toggleCameraLock={toggleCameraLock}
                cameraLocked={state.cameraLocked}
                isTour={state.isTour}
                reviewMode={reviewMode}
                editing={showEditor}
                toggleGallery={toggleGallery}
                animationName={state.name}
              />
              <EditActionHeader editing={showEditor} toggleGallery={toggleGallery} animationName={state.name} keyframes={state.keyframes} />
            </div>
          )}
          <KeyframeContainer
            keyframes={state.keyframes}
            editing={showEditor}
            toggleGallery={toggleGallery}
            currentKeyframe={state.currentKeyframe}
          />
        </>
      )}
      {!isDocked && <GalleryFooter />}
    </>
  );

  // When docked, render without Draggable wrapper
  if (isDocked) {
    return (
      <div className={`GalleryContainer docked visible`}>
        {galleryContent}
      </div>
    );
  }

  // Minimized floating gallery
  if (galleryMinimized) {
    return (
      <Draggable handle=".GalleryHeader" nodeRef={draggableRef}>
        <div ref={draggableRef} className={`GalleryContainer${galleryVisible ? " visible" : ""}${galleryMinimized ? " minimized" : ""}`}>
          <GalleryHeader
            galleryMinimized={galleryMinimized}
            editing={showEditor}
            toggleGalleryMin={toggleGalleryMin}
            toggleGallery={toggleGallery}
            animationName={state.name}
            isDocked={isDocked}
            onDock={handleDock}
            onCloseToDock={handleCloseToDock}
          />
        </div>
      </Draggable>
    );
  }

  // Floating gallery
  return (
    <Draggable handle=".GalleryHeader, .GalleryFooter" nodeRef={draggableRef}>
      <div ref={draggableRef} className={`GalleryContainer${galleryVisible ? " visible" : ""}${galleryMinimized ? " minimized" : ""}`}>
        {galleryContent}
      </div>
    </Draggable>
  );
}

function GalleryFooter() {
  return <div className="GalleryFooter" />;
}

function GalleryHeader({ galleryMinimized, editing, animationName, toggleGalleryMin, toggleGallery, isDocked, onDock, onCloseToDock }) {
  return (
    <div className="GalleryHeader">
      <GalleryTitle galleryMinimized={galleryMinimized} editing={editing} animationName={animationName} isDocked={isDocked} />
      <WindowControls 
        galleryMinimized={galleryMinimized} 
        toggleGalleryMin={toggleGalleryMin} 
        toggleGallery={toggleGallery}
        isDocked={isDocked}
        onDock={onDock}
        onCloseToDock={onCloseToDock}
      />
    </div>
  );
}

function WindowControls({ galleryMinimized, toggleGalleryMin, toggleGallery, isDocked, onDock, onCloseToDock }) {
  return (
    <div className="windowControlsContainer">
      {!isDocked && onDock && <GalleryDock onDock={onDock} />}
      {!isDocked && onCloseToDock && <GalleryClose onCloseToDock={onCloseToDock} />}
    </div>
  );
}

function EditAnimationHeader({ editing, toggleCameraLock, cameraLocked, isTour, reviewMode, toggleGallery, animationName }) {
  return (
    <div className="EditHeader">
      {editing && (
        <EditAnimationControls
          toggleCameraLock={toggleCameraLock}
          cameraLocked={cameraLocked}
          isTour={isTour}
          toggleGallery={toggleGallery}
          animationName={animationName}
        />
      )}
    </div>
  );
}

function EditActionHeader({ editing, toggleGallery, animationName, keyframes }) {
  return (
    <div className="EditHeader EditActionHeader">
      {editing && (
        <EditActionControls toggleGallery={toggleGallery} animationName={animationName} keyframes={keyframes} />
      )}
    </div>
  );
}

function EditAnimationControls({ isTour, toggleCameraLock, cameraLocked }) {
  const [include3D, setInclude3D] = useState(true);

  const delAnimClick = (e) => {
    e.stopPropagation();
    Inventum.animations.deleteAnimation();
  };

  const cloneAnimationClick = (e) => {
    e.stopPropagation();
    Inventum.animations.cloneAnimation();
  };

  const setTourClick = (e) => {
    if (isTour) {
      return;
    }
    e.stopPropagation();
    Inventum.animations.setTour();
  };

  const handleFinishIn3DClick = (e) => {
    e.stopPropagation();
    setInclude3D(!include3D);
    animations.finishIn3D = !animations.finishIn3D;
  };

  return (
    <div className="EditControls AnimationControls">
      <div className="EditControlSection">
        <div className="EditControlSectionLabel">Animation</div>
        <div className="EditControlSectionButtons">
          <div title="Clone Animation" className="editPillButton" onClick={cloneAnimationClick}>
            Clone
          </div>
          <div title="Deletes the animation. Warning can't be undone!" className="editPillButton danger" onClick={delAnimClick}>
            Delete
          </div>
        </div>
      </div>
      
      <div className="EditControlSection">
        <div className="EditControlSectionLabel">Settings</div>
        <div className="EditControlSectionButtons">
          <div 
            title="Toggle camera lock. Unlocked means the camera won't move during the animation."
            className={`editPillButton ${cameraLocked ? 'active' : ''}`}
            onClick={toggleCameraLock}>
            {cameraLocked ? "Locked" : "Unlocked"}
          </div>
          <div
            title="Whilst enabled, the Finish in 3D option will place you in the 3D world after your final animation tour slide."
            className={`editPillButton ${include3D ? 'active' : ''}`}
            onClick={handleFinishIn3DClick}>
            Finish 3D
          </div>
          <div 
            title="Change Project Tour" 
            className={`editPillButton ${isTour ? 'active disabled' : ''}`} 
            onClick={setTourClick}>
            {isTour ? "Is Tour" : "Set Tour"}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditActionControls() {
  const cameraClick = useCallback((e) => {
    e?.stopPropagation?.();
    Inventum.animations.setCamera();
  }, []);

  const objectsClick = useCallback((e) => {
    e?.stopPropagation?.();
    Inventum.animations.setScenestate();
  }, []);

  const updateAllClick = useCallback((e) => {
    e?.stopPropagation?.();
    Inventum.animations.updateKeyframe();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (shouldIgnoreShortcut(e)) {
        return;
      }

      if (isAltKeyShortcut(e, "KeyC")) {
        cameraClick();
      } else if (isAltKeyShortcut(e, "KeyT")) {
        objectsClick();
      } else if (isAltKeyShortcut(e, "KeyU")) {
        updateAllClick();
      } else {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [cameraClick, objectsClick, updateAllClick]);

  const handleCopyView = (e) => {
    e.stopPropagation();
    Inventum.animations.storeCameraClipboard();
  };

  const handlePasteView = (e) => {
    e.stopPropagation();
    Inventum.animations.setCameraFromClipboard();
  };

  const handleCopySceneState = (e) => {
    e.stopPropagation();
    Inventum.animations.storeScenestateClipboard();
  };

  const handlePasteSceneState = (e) => {
    e.stopPropagation();
    Inventum.animations.setScenestateFromClipboard();
  };

  return (
    <div className="EditControls ActionControls">
      <div className="EditControlSection">
        <div className="EditControlSectionLabel">Camera</div>
        <div className="EditControlSectionButtons">
          <div className="editPillButton" title={`Sets the current camera position for the active action (${CAMERA_SET_SHORTCUT})`} onClick={cameraClick}>
            Set
          </div>
          <div className="editPillButton" title="Copies current view" onClick={handleCopyView}>
            Copy
          </div>
          <div className="editPillButton" title="Pastes previously copied view" onClick={handlePasteView}>
            Paste
          </div>
        </div>
      </div>
      
      <div className="EditControlSection">
        <div className="EditControlSectionLabel">State</div>
        <div className="EditControlSectionButtons">
          <div className="editPillButton" title={`Sets the objects in the scenestate for the active action (${STATE_SET_SHORTCUT})`} onClick={objectsClick}>
            Set
          </div>
          <div className="editPillButton" title="Copies current scenestate" onClick={handleCopySceneState}>
            Copy
          </div>
          <div className="editPillButton" title="Pastes previously copied scenestate" onClick={handlePasteSceneState}>
            Paste
          </div>
        </div>
      </div>
      
      <div className="editPillButton primary" title={`Updates Both Camera and Scene State (${UPDATE_BOTH_SHORTCUT})`} onClick={updateAllClick}>
        Update Both
      </div>
    </div>
  );
}

function GalleryTitle({ galleryMinimized, editing, animationName, isDocked }) {
  const isEditable = editing && !galleryMinimized;

  const handleClick = () => {
    if (isEditable) {
      Inventum.animations.changeAnimationName();
    }
  };

  const handleKeyDown = (e) => {
    if (!isEditable) {
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      Inventum.animations.changeAnimationName();
    }
  };

  return (
    <div
      className={`GalleryTitle${galleryMinimized ? " minimized" : ""}${isDocked ? " docked" : ""}${isEditable ? " editable" : ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isEditable ? "button" : undefined}
      tabIndex={isEditable ? 0 : undefined}
      title={isEditable ? "Click to change animation title" : "Animation Title"}
    >
      {animationName}
    </div>
  );
}

function GalleryDock({ onDock }) {
  return (
    <div className="titlebarDockButton" onClick={onDock} onTouchStart={onDock} title="Dock to right side">
      <i className={DOCK_TO_LEFT_ICON_CLASS}>{DOCK_TO_LEFT_ICON}</i>
    </div>
  );
}

function GalleryClose({ onCloseToDock }) {
  const handleClose = (e) => {
    e.stopPropagation();
    onCloseToDock();
  };

  return (
    <div className="titlebarCloseButton" onClick={handleClose} onTouchStart={handleClose} title="Close to dock">
      <i className="material-icons notranslate">close</i>
    </div>
  );
}

function GalleryMinimize({ galleryMinimized, toggleGalleryMin }) {
  const handleClose = () => {
    toggleGalleryMin();
  };

  return (
    <div className="titlebarMinMaxButton" onClick={handleClose} onTouchStart={handleClose}>
      <i className="material-icons notranslate">{galleryMinimized ? "crop_square" : "remove"}</i>
    </div>
  );
}

export default GalleryContainer;
